import { useState, useEffect, useRef } from 'react';
import { getScoreColor } from '../utils/scoring';
import TickSlider from './TickSlider';

// Stopwatch hook
function useStopwatch() {
  const [timers, setTimers] = useState({});
  const intervalsRef = useRef({});

  const start = (id) => {
    if (intervalsRef.current[id]) return;
    const startTime = Date.now() - (timers[id]?.elapsed || 0);
    intervalsRef.current[id] = setInterval(() => {
      setTimers(prev => ({ ...prev, [id]: { ...prev[id], elapsed: Date.now() - startTime, running: true } }));
    }, 100);
    setTimers(prev => ({ ...prev, [id]: { ...prev[id], running: true, startTime } }));
  };

  const stop = (id) => {
    if (intervalsRef.current[id]) {
      clearInterval(intervalsRef.current[id]);
      delete intervalsRef.current[id];
    }
    setTimers(prev => ({ ...prev, [id]: { ...prev[id], running: false } }));
  };

  const reset = (id) => {
    stop(id);
    setTimers(prev => ({ ...prev, [id]: { elapsed: 0, running: false } }));
  };

  const formatTime = (ms) => {
    if (!ms) return '0:00';
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    return () => Object.values(intervalsRef.current).forEach(clearInterval);
  }, []);

  return { timers, start, stop, reset, formatTime };
}

// Noise meter component
function NoiseMeter({ onCapture, existingLevel }) {
  const [measuring, setMeasuring] = useState(false);
  const [level, setLevel] = useState(existingLevel || null);
  const [samples, setSamples] = useState([]);
  const audioRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  const startMeasure = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioRef.current = { stream, ctx };
      analyserRef.current = analyser;
      setMeasuring(true);
      setSamples([]);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let sampleList = [];
      const startTime = Date.now();

      const measure = () => {
        if (Date.now() - startTime > 10000) {
          const avg = sampleList.length > 0 ? sampleList.reduce((a, b) => a + b, 0) / sampleList.length : 0;
          const dbLevel = Math.round(avg);
          setLevel(dbLevel);
          onCapture(dbLevel);
          stopMeasure();
          return;
        }
        analyser.getByteFrequencyData(dataArray);
        const rms = Math.sqrt(dataArray.reduce((s, v) => s + v * v, 0) / dataArray.length);
        const db = Math.round(20 * Math.log10(Math.max(rms, 1)));
        sampleList.push(db);
        setSamples([...sampleList]);
        rafRef.current = requestAnimationFrame(measure);
      };
      measure();
    } catch (e) {
      console.warn('Mic access denied');
      setMeasuring(false);
    }
  };

  const stopMeasure = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioRef.current) {
      audioRef.current.stream.getTracks().forEach(t => t.stop());
      audioRef.current.ctx.close();
    }
    setMeasuring(false);
  };

  const getLabel = (db) => db < 40 ? 'Very Quiet' : db < 55 ? 'Quiet' : db < 65 ? 'Moderate' : db < 75 ? 'Lively' : db < 85 ? 'Loud' : 'Very Loud';
  const getColor = (db) => db < 55 ? 'text-emerald-400' : db < 70 ? 'text-brand-gold' : db < 80 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="bg-neutral-800/30 rounded-lg p-3 mt-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-neutral-500 font-medium">Noise Level</span>
        {level !== null && <span className={`text-sm font-bold ${getColor(level)}`}>{level}dB — {getLabel(level)}</span>}
      </div>
      {measuring ? (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] text-red-400">Measuring... {samples.length > 0 ? `${Math.round(samples[samples.length - 1])}dB` : ''}</span>
            <span className="text-[9px] text-neutral-600">{Math.min(10, Math.floor(samples.length / 10))}s / 10s</span>
          </div>
          <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
            <div className="h-full bg-red-500 rounded-full transition-all" style={{ width: `${Math.min(100, samples.length)}%` }} />
          </div>
        </div>
      ) : (
        <button onClick={startMeasure} className="text-[11px] sm:text-[10px] px-3 py-2 sm:py-1.5 rounded bg-neutral-800/50 text-neutral-500 hover:text-white border border-neutral-700 active:bg-neutral-800 transition-colors">
          {level !== null ? 'Measure Again' : 'Measure Noise Level'}
        </button>
      )}
    </div>
  );
}

const JOURNEY_PHASES = [
  {
    phase: 'Pre-Visit',
    color: '#60a5fa',
    weight: 0.75,
    touchpoints: [
      { id: 'awareness', label: 'Awareness / Discovery', icon: '🔍', aiField: true, desc: 'How do guests first hear about this place?' },
      { id: 'googleSearch', label: 'Google Search & Listing', icon: '🔎', aiField: true, desc: 'Search ranking, business profile, photos, hours accuracy' },
      { id: 'websiteVisit', label: 'Website Experience', icon: '💻', aiField: true, desc: 'Design, speed, mobile, menu accessibility, online ordering' },
      { id: 'socialScroll', label: 'Social Media Presence', icon: '📱', aiField: true, desc: 'Content quality, frequency, engagement, vibe conveyed' },
      { id: 'reviewRead', label: 'Reading Reviews', icon: '⭐', aiField: true, desc: 'Themes, sentiment, management response quality' },
      { id: 'reservation', timed: true, label: 'Booking / Ordering', icon: '📞', aiField: true, desc: 'Reservation, online order, or walk-in — ease and clarity' },
      { id: 'deliveryPresence', label: 'Third-Party Platforms', icon: '📦', aiField: true, desc: 'Delivery app listings, photos, menu accuracy, ratings' },
    ],
  },
  {
    phase: 'Arrival',
    color: '#C9A668',
    weight: 1.0,
    touchpoints: [
      { id: 'neighborhood', label: 'Neighborhood & Approach', icon: '🏙️', desc: 'What does the area feel like approaching?' },
      { id: 'parkingValet', label: 'Parking / Valet / Access', icon: '🅿️', desc: 'Ease of parking, valet availability, transit access, signage to entrance' },
      { id: 'exterior', label: 'Exterior & Signage', icon: '🪧', desc: 'First visual impression from outside, branding, cleanliness' },
      { id: 'entrance', label: 'Entrance Experience', icon: '🚪', desc: 'Doorway, lighting, music bleed, first scent, energy' },
      { id: 'hostGreeting', timed: true, label: 'Host / First Greeting', icon: '👋', desc: 'Acknowledgment speed, warmth, professionalism' },
      { id: 'waitExperience', timed: true, label: 'Wait Experience', icon: '⏱️', desc: 'If waiting: bar option, communication, comfort, time estimate' },
      { id: 'pickupHandoff', label: 'Pickup / To-Go Handoff', icon: '🛍️', desc: 'Designated area, signage, speed, order accuracy, packaging' },
      { id: 'seatingWalk', label: 'Walk to Seat', icon: '🚶', desc: 'What do you see, hear, smell on the way?' },
    ],
  },
  {
    phase: 'The Setting',
    color: '#34d399',
    weight: 1.0,
    touchpoints: [
      { id: 'tableFirst', label: 'Seat First Impression', icon: '🪑', desc: 'Comfort, cleanliness, view, spacing, table setting' },
      { id: 'ambiance', noiseMeter: true, label: 'Ambiance & Energy', icon: '✨', desc: 'Lighting, music, temperature, crowd energy, scent' },
      { id: 'serverIntro', timed: true, label: 'Server / Staff Introduction', icon: '🤝', desc: 'Timing, personality, knowledge, initial recommendations' },
      { id: 'menuPresentation', label: 'Menu Presentation', icon: '📖', desc: 'Physical/digital menu quality, organization, storytelling, specials' },
      { id: 'menuAccuracy', label: 'Menu Accuracy', icon: '✅', desc: 'Does menu match online? Prices current? Items available?' },
      { id: 'waterBread', timed: true, label: 'Opening Gesture', icon: '💧', desc: 'Water, bread, amuse-bouche, complimentary offering — timing and quality' },
      { id: 'familyAccommodation', label: 'Family / Group Accommodation', icon: '👨‍👩‍👧', desc: 'Kids menu, highchairs, large party handling, accessibility' },
    ],
  },
  {
    phase: 'Food & Beverage',
    color: '#fb923c',
    weight: 1.5,
    touchpoints: [
      { id: 'firstBeverage', timed: true, label: 'First Beverage', icon: '🥤', desc: 'Speed, quality, presentation — coffee, cocktail, water, whatever comes first' },
      { id: 'appetizerCourse', timed: true, label: 'Starter / Snack / Shared', icon: '🥗', desc: 'Timing, temperature, presentation, flavor, portion' },
      { id: 'entreeCourse', timed: true, label: 'Main Course', icon: '🍽️', desc: 'Execution, temperature, flavor, portion, timing from starter' },
      { id: 'foodPresentation', label: 'Plating & Presentation', icon: '🎨', desc: 'Visual appeal, Instagram-worthiness, attention to detail' },
      { id: 'beverageThroughout', label: 'Beverage Service Throughout', icon: '🍷', desc: 'Refills, pacing, wine service, pairing suggestions, upsells' },
      { id: 'barProgram', label: 'Bar / Beverage Program', icon: '🍸', desc: 'Cocktail creativity, menu clarity, happy hour execution, glassware' },
      { id: 'pacing', label: 'Course Pacing', icon: '⏳', desc: 'Too fast, too slow, or perfectly timed between courses?' },
      { id: 'dessertOffer', timed: true, label: 'Dessert / Final Course', icon: '🍰', desc: 'The offer, quality, after-dinner beverage suggestion' },
    ],
  },
  {
    phase: 'Service & Hospitality',
    color: '#c084fc',
    weight: 1.5,
    touchpoints: [
      { id: 'serviceSpeed', timed: true, label: 'Speed of Service', icon: '⚡', desc: 'Time from order to first course, from sit to first beverage' },
      { id: 'attentiveness', label: 'Attentiveness', icon: '👁️', desc: 'Reading the table, anticipating needs without hovering' },
      { id: 'checkIns', label: 'Check-ins & Timing', icon: '🔄', desc: 'Frequency, timing, genuineness of check-ins' },
      { id: 'teamKnowledge', label: 'Team Knowledge', icon: '🧠', desc: 'Ingredients, sourcing, allergens, wine pairings, menu fluency' },
      { id: 'specialRequests', label: 'Special Requests & Allergies', icon: '✋', desc: 'How are modifications, dietary needs, and allergies handled?' },
      { id: 'managerPresence', label: 'Manager / Owner Presence', icon: '👔', desc: 'On the floor? Touching tables? Visible leadership?' },
      { id: 'restroomVisit', label: 'Restroom Experience', icon: '🚻', desc: 'Cleanliness, supplies, design, scent, maintenance' },
      { id: 'problemRecovery', label: 'Problem Recovery', icon: '🔧', desc: 'If something went wrong — speed, empathy, resolution quality' },
    ],
  },
  {
    phase: 'Departure',
    color: '#f87171',
    weight: 1.0,
    touchpoints: [
      { id: 'checkPresentation', timed: true, label: 'Check Presentation', icon: '💳', desc: 'Timing, accuracy, ease of payment, splitting, tipping prompt' },
      { id: 'takeawayPackaging', label: 'Takeaway / Leftovers', icon: '📦', desc: 'Offered proactively? Packaging quality? Branded?' },
      { id: 'coatCheckValet', timed: true, label: 'Coat Check / Valet Return', icon: '🧥', desc: 'Speed, organization, final service touch' },
      { id: 'farewell', label: 'Farewell', icon: '👋', desc: 'Thank you, eye contact, door opening, warmth, personal touch' },
      { id: 'lastImpression', label: 'Last Impression', icon: '🌙', desc: 'What feeling do you walk away with?' },
    ],
  },
  {
    phase: 'Post-Visit',
    color: '#06b6d4',
    weight: 0.75,
    touchpoints: [
      { id: 'followUp', label: 'Follow-Up Communication', icon: '📧', aiField: true, desc: 'Email, text, review request, return offer, CRM capture' },
      { id: 'loyaltyCRM', label: 'Loyalty / CRM / Retention', icon: '💎', aiField: true, desc: 'Guest info captured? Membership? Return incentives? Personalization?' },
      { id: 'socialPrompt', label: 'Social Shareability', icon: '📸', aiField: true, desc: 'Photo-worthy moments? Encouraged to post/tag? Hashtag visible?' },
      { id: 'reviewResponse', label: 'Review Response Quality', icon: '💬', aiField: true, desc: 'How does the business respond to reviews? Personal or templated?' },
      { id: 'memoryAt48hrs', label: 'The 48-Hour Memory', icon: '🧠', desc: 'What would you remember 2 days later?' },
      { id: 'wouldReturn', label: 'Would You Return?', icon: '🔄', desc: 'Likelihood of return visit and why' },
      { id: 'wouldRecommend', label: 'Would You Recommend?', icon: '💬', desc: 'Likelihood of telling a friend or posting about it' },
    ],
  },
];

// Export for use in other components
export { JOURNEY_PHASES };

const DEFAULT_TEAM = ['Unassigned'];
const TEAM_ROSTER = ['Alfio', 'Cari', 'Bill', 'Dan'];

export default function GuestJourney({ audit, onUpdate }) {
  const [expandedPhase, setExpandedPhase] = useState('Pre-Visit');
  const [editingVisitName, setEditingVisitName] = useState(false);
  const [newMember, setNewMember] = useState('');
  const { timers, start: startTimer, stop: stopTimer, reset: resetTimer, formatTime } = useStopwatch();

  // Team members — stored on audit, editable
  const teamMembers = audit.journeyTeam || DEFAULT_TEAM;

  // Support multiple visits — backward compat with single guestJourney
  const visits = Array.isArray(audit.guestJourneys) && audit.guestJourneys.length > 0
    ? audit.guestJourneys
    : audit.guestJourney && Object.keys(audit.guestJourney).length > 0
      ? [{ name: 'Visit 1', data: audit.guestJourney, createdAt: audit.createdAt }]
      : [{ name: 'Visit 1', data: {}, createdAt: new Date().toISOString() }];

  const activeIndex = audit._activeVisitIndex || 0;
  const safeIndex = Math.min(activeIndex, visits.length - 1);
  const journey = visits[safeIndex]?.data || {};
  const visitName = visits[safeIndex]?.name || `Visit ${safeIndex + 1}`;

  const saveVisits = (updated, newActiveIndex) => {
    onUpdate({
      ...audit,
      guestJourneys: updated,
      guestJourney: updated[newActiveIndex !== undefined ? newActiveIndex : safeIndex]?.data || {},
      _activeVisitIndex: newActiveIndex !== undefined ? newActiveIndex : safeIndex,
    });
  };

  const updateTouchpoint = (id, field, value) => {
    const updated = [...visits];
    const tpData = { ...journey[id], [field]: value };
    // Mark as scored when slider is moved
    if (field === 'score') tpData.scored = true;
    updated[safeIndex] = {
      ...updated[safeIndex],
      data: { ...journey, [id]: tpData },
    };
    saveVisits(updated);
  };

  const addVisit = () => {
    const newVisit = { name: `Visit ${visits.length + 1}`, data: {}, date: new Date().toISOString().split('T')[0], createdAt: new Date().toISOString() };
    const updated = [...visits, newVisit];
    saveVisits(updated, updated.length - 1);
  };

  const deleteVisit = (index) => {
    if (visits.length <= 1) {
      // Reset the only visit instead of deleting
      saveVisits([{ name: 'Visit 1', data: {}, date: new Date().toISOString().split('T')[0], createdAt: new Date().toISOString() }], 0);
      return;
    }
    const updated = visits.filter((_, i) => i !== index);
    const newActive = Math.min(safeIndex, updated.length - 1);
    saveVisits(updated, newActive);
  };

  const renameVisit = (index, name) => {
    const updated = [...visits];
    updated[index] = { ...updated[index], name };
    saveVisits(updated);
  };

  const updateVisitField = (index, field, value) => {
    const updated = [...visits];
    updated[index] = { ...updated[index], [field]: value };
    saveVisits(updated);
  };

  const switchVisit = (index) => {
    onUpdate({ ...audit, _activeVisitIndex: index, guestJourney: visits[index]?.data || {} });
  };

  const duplicateVisit = (index) => {
    const source = visits[index];
    const newVisit = { ...source, name: `${source.name} (copy)`, data: { ...source.data }, createdAt: new Date().toISOString() };
    const updated = [...visits, newVisit];
    saveVisits(updated, updated.length - 1);
  };

  const copyToVisit = (tpId, targetIndex) => {
    const updated = [...visits];
    const sourceData = journey[tpId];
    if (!sourceData) return;
    updated[targetIndex] = {
      ...updated[targetIndex],
      data: { ...(updated[targetIndex].data || {}), [tpId]: { ...sourceData } },
    };
    saveVisits(updated);
  };

  // Three states per touchpoint:
  // 1. Default (no data / not evaluated) — doesn't count toward anything
  // 2. Scored (scored: true) — counts toward progress AND score average
  // 3. Not Offered (notOffered: true) — counts toward progress, NOT score
  // States: default (unevaluated), aiSuggested (AI filled but not approved), scored (team approved), notOffered
  const getState = (tp) => {
    const d = journey[tp.id];
    if (!d) return 'default';
    if (d.notOffered || d.na) return 'notOffered';
    if (d.approved) return 'scored'; // Team has approved this score
    // AI filled but not yet approved — show as suggestion
    if (d.scored || d.score > 0 || d.aiScore > 0) return 'aiSuggested';
    return 'default';
  };

  const allTPs = JOURNEY_PHASES.flatMap(p => p.touchpoints);
  const approvedCount = allTPs.filter(t => getState(t) === 'scored').length;
  const notOfferedCount = allTPs.filter(t => getState(t) === 'notOffered').length;
  const aiPendingCount = allTPs.filter(t => getState(t) === 'aiSuggested').length;
  const evaluatedCount = approvedCount + notOfferedCount;
  const totalCount = allTPs.length;

  // Weighted average: each phase's average is weighted by phase.weight
  const avgScore = (() => {
    let weightedSum = 0, totalWeight = 0;
    JOURNEY_PHASES.forEach(phase => {
      const pApproved = phase.touchpoints.filter(t => getState(t) === 'scored');
      if (pApproved.length > 0) {
        const phaseAvg = pApproved.reduce((s, t) => s + (journey[t.id]?.score || 0), 0) / pApproved.length;
        weightedSum += phaseAvg * (phase.weight || 1.0);
        totalWeight += (phase.weight || 1.0);
      }
    });
    return totalWeight > 0 ? weightedSum / totalWeight : 0;
  })();

  return (
    <div>
      {/* Visit Selector */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {visits.map((v, i) => (
          <button
            key={i}
            onClick={() => switchVisit(i)}
            className={`text-xs px-3 py-1.5 rounded-lg transition-all ${i === safeIndex ? 'bg-brand-gold/20 text-brand-gold border border-brand-gold/40 font-semibold' : 'bg-neutral-800/40 text-neutral-500 hover:text-neutral-300 border border-neutral-800'}`}
          >
            <span>{v.name}</span>
            {(v.mealPeriod || v.seating) && <span className="text-[9px] ml-1 opacity-70">{[v.mealPeriod, v.seating].filter(Boolean).join(' · ')}</span>}
            {v.date && <span className="text-[9px] ml-1 opacity-50">{new Date(v.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
          </button>
        ))}
        <button onClick={addVisit} className="text-xs px-2 py-1.5 rounded-lg bg-neutral-800/20 text-neutral-600 hover:text-neutral-400 border border-dashed border-neutral-700">+ New Visit</button>
      </div>

      {/* Visit Header — stacks on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          {editingVisitName ? (
            <input
              type="text"
              value={visitName}
              onChange={e => renameVisit(safeIndex, e.target.value)}
              onBlur={() => setEditingVisitName(false)}
              onKeyDown={e => e.key === 'Enter' && setEditingVisitName(false)}
              autoFocus
              className="input-field text-base font-semibold py-2 w-full sm:w-48"
            />
          ) : (
            <h2 className="font-display text-xl text-white cursor-pointer hover:text-brand-gold transition-colors" onClick={() => setEditingVisitName(true)}>
              {visitName}
            </h2>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={visits[safeIndex]?.date || ''}
              onChange={e => updateVisitField(safeIndex, 'date', e.target.value)}
              className="text-sm sm:text-[10px] px-3 sm:px-2 py-2 sm:py-0.5 rounded bg-neutral-800 text-white border-0 focus:ring-1 focus:ring-brand-gold/30"
            />
            <select
              value={visits[safeIndex]?.mealPeriod || ''}
              onChange={e => updateVisitField(safeIndex, 'mealPeriod', e.target.value)}
              className="text-sm sm:text-[10px] px-3 sm:px-2 py-2 sm:py-1 rounded bg-neutral-800 text-white border-0 focus:ring-1 focus:ring-brand-gold/30"
            >
              <option value="">Meal Period...</option>
              <option value="Breakfast">Breakfast</option>
              <option value="Brunch">Brunch</option>
              <option value="Lunch">Lunch</option>
              <option value="Dinner">Dinner</option>
              <option value="Happy Hour">Happy Hour</option>
              <option value="Special Event">Special Event</option>
              <option value="Late Night">Late Night</option>
            </select>
            <select
              value={visits[safeIndex]?.seating || ''}
              onChange={e => updateVisitField(safeIndex, 'seating', e.target.value)}
              className="text-sm sm:text-[10px] px-3 sm:px-2 py-2 sm:py-1 rounded bg-neutral-800 text-white border-0 focus:ring-1 focus:ring-brand-gold/30"
            >
              <option value="">Seating...</option>
              <option value="Dining Seating">Dining Seating</option>
              <option value="Lounge Seating">Lounge Seating</option>
              <option value="Bar">Bar</option>
              <option value="Pick Up">Pick Up</option>
              <option value="Outdoor Seating">Outdoor Seating</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-3">
          <div className="flex gap-2">
            <button onClick={() => duplicateVisit(safeIndex)} className="text-[10px] px-3 py-1.5 sm:px-2 sm:py-1 rounded bg-neutral-800/40 text-neutral-500 hover:text-neutral-300 transition-colors">Duplicate</button>
            <button onClick={() => { if (window.confirm(`Delete "${visitName}"?`)) deleteVisit(safeIndex); }} className="text-[10px] px-3 py-1.5 sm:px-2 sm:py-1 rounded bg-neutral-800/40 text-neutral-500 hover:text-red-400 transition-colors">Delete</button>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-black ${getScoreColor(avgScore).text}`}>{avgScore > 0 ? avgScore.toFixed(1) : '—'}</p>
            <p className="text-[10px] text-neutral-500">
              {approvedCount} approved{notOfferedCount > 0 ? ` · ${notOfferedCount} N/A` : ''}{aiPendingCount > 0 ? ` · ${aiPendingCount} pending` : ''} / {totalCount}
            </p>
          </div>
        </div>
      </div>

      {/* Team members — for touchpoint assignment */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-[10px] text-neutral-500 font-medium">Team:</span>
        {teamMembers.filter(m => m !== 'Unassigned').map((m, i) => (
          <span key={i} className="text-[10px] px-2.5 py-1 rounded-lg bg-neutral-800/50 text-neutral-300 flex items-center gap-1.5 border border-neutral-700">
            {m}
            <button onClick={() => onUpdate({ ...audit, journeyTeam: teamMembers.filter((_, j) => j !== (i + 1)) })} className="text-neutral-600 hover:text-red-400 text-xs">×</button>
          </span>
        ))}
        <select
          onChange={e => {
            if (!e.target.value) return;
            if (!teamMembers.includes(e.target.value)) {
              onUpdate({ ...audit, journeyTeam: [...teamMembers, e.target.value] });
            }
            e.target.value = '';
          }}
          className="text-[10px] px-3 py-1 rounded-lg bg-neutral-800/40 text-neutral-500 border border-dashed border-neutral-700 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/30 w-36"
          defaultValue=""
        >
          <option value="">+ Add Team Member</option>
          {TEAM_ROSTER.filter(m => !teamMembers.includes(m)).map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {/* Phase progress bar — scrollable on mobile */}
      <div className="overflow-x-auto scrollbar-hide -mx-1 px-1 mb-6">
        <div className="flex gap-1 min-w-max sm:min-w-0">
          {JOURNEY_PHASES.map(phase => {
            const phaseTPs = phase.touchpoints;
            const phaseCompleted = phaseTPs.filter(t => getState(t) === 'scored' || getState(t) === 'notOffered').length;
            const pct = phaseTPs.length > 0 ? (phaseCompleted / phaseTPs.length) * 100 : 0;
            return (
              <button
                key={phase.phase}
                onClick={() => setExpandedPhase(expandedPhase === phase.phase ? null : phase.phase)}
                className="flex-1 min-w-[70px] group"
                title={`${phase.phase}: ${phaseCompleted}/${phaseTPs.length}`}
              >
                <div className="h-3 sm:h-2 bg-neutral-800 rounded-full overflow-hidden mb-1">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: phase.color }} />
                </div>
                <p className={`text-[9px] sm:text-[8px] text-center truncate ${expandedPhase === phase.phase ? 'text-white font-bold' : 'text-neutral-600 group-hover:text-neutral-400'}`}>{phase.phase}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Phases */}
      {JOURNEY_PHASES.map(phase => (
        <div key={phase.phase} className={`mb-4 ${expandedPhase === phase.phase ? '' : 'hidden'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: phase.color }} />
              <h3 className="text-sm font-semibold text-white">{phase.phase}</h3>
              <span className="text-[9px] text-neutral-600">{phase.weight}x weight</span>
            </div>
            <div className="flex gap-1">
              {phase.touchpoints.some(tp => getState(tp) === 'aiSuggested') && (
                <button
                  onClick={() => {
                    const updated = [...visits];
                    const newData = { ...journey };
                    phase.touchpoints.forEach(tp => {
                      if (getState(tp) === 'aiSuggested') {
                        newData[tp.id] = { ...newData[tp.id], approved: true, scored: true };
                      }
                    });
                    updated[safeIndex] = { ...updated[safeIndex], data: newData };
                    saveVisits(updated);
                  }}
                  className="text-[9px] px-2 py-1 rounded bg-brand-gold/10 text-brand-gold border border-brand-gold/30 hover:bg-brand-gold/20 transition-colors"
                >
                  Approve All
                </button>
              )}
              {phase.touchpoints.some(tp => getState(tp) === 'default') && (
                <button
                  onClick={() => {
                    const updated = [...visits];
                    const newData = { ...journey };
                    phase.touchpoints.forEach(tp => {
                      if (getState(tp) === 'default') {
                        newData[tp.id] = { ...newData[tp.id], notOffered: true, na: true };
                      }
                    });
                    updated[safeIndex] = { ...updated[safeIndex], data: newData };
                    saveVisits(updated);
                  }}
                  className="text-[9px] px-2 py-1 rounded bg-neutral-800/40 text-neutral-600 hover:text-neutral-400 transition-colors"
                >
                  N/A All
                </button>
              )}
              {teamMembers.length > 1 && (
                <select
                  onChange={e => {
                    if (!e.target.value) return;
                    const updated = [...visits];
                    const newData = { ...journey };
                    phase.touchpoints.forEach(tp => {
                      newData[tp.id] = { ...newData[tp.id], assignedTo: e.target.value };
                    });
                    updated[safeIndex] = { ...updated[safeIndex], data: newData };
                    saveVisits(updated);
                    e.target.value = '';
                  }}
                  className="text-[9px] px-2 py-1 rounded bg-neutral-800/40 text-neutral-600 border-0 hover:text-neutral-400 transition-colors"
                  defaultValue=""
                >
                  <option value="">Assign Phase...</option>
                  {teamMembers.filter(m => m !== 'Unassigned').map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="space-y-2">
            {phase.touchpoints.map(tp => {
              const data = journey[tp.id] || {};
              const state = getState(tp);
              const score = data.score || 0;
              const color = getScoreColor(score);

              const approveTP = () => {
                const updated = [...visits];
                updated[safeIndex] = {
                  ...updated[safeIndex],
                  data: { ...journey, [tp.id]: { ...data, approved: true, scored: true, notOffered: false, na: false } },
                };
                saveVisits(updated);
              };

              const markNotOffered = () => {
                const updated = [...visits];
                updated[safeIndex] = {
                  ...updated[safeIndex],
                  data: { ...journey, [tp.id]: { ...data, notOffered: true, approved: false, scored: false, na: true } },
                };
                saveVisits(updated);
              };

              const resetTP = () => {
                const updated = [...visits];
                updated[safeIndex] = {
                  ...updated[safeIndex],
                  data: { ...journey, [tp.id]: { ...data, notOffered: false, approved: false, scored: false, na: false } },
                };
                saveVisits(updated);
              };

              const activateTP = () => {
                const updated = [...visits];
                updated[safeIndex] = {
                  ...updated[safeIndex],
                  data: { ...journey, [tp.id]: { ...data, approved: true, scored: true, notOffered: false, na: false } },
                };
                saveVisits(updated);
              };

              return (
                <div key={tp.id} className={`card p-4 sm:p-3 transition-all ${state === 'aiSuggested' ? 'border border-brand-gold/20' : ''}`}>
                  {/* Top row: icon, label, score */}
                  <div className="flex items-center gap-2 mb-2 sm:mb-1">
                    <span className="text-lg sm:text-sm">{tp.icon}</span>
                    <span className="text-sm sm:text-xs font-medium flex-1 text-white">{tp.label}</span>
                    {(state === 'scored' || state === 'aiSuggested') && data.aiScore > 0 && (
                      <span className={`text-[10px] sm:text-[9px] px-1.5 py-0.5 rounded bg-brand-gold/10 ${getScoreColor(data.aiScore).text}`}>
                        AI: {data.aiScore.toFixed(1)}
                      </span>
                    )}
                    {state === 'scored' && <span className={`text-base sm:text-xs font-bold ${color.text}`}>{score.toFixed(1)}</span>}
                    {state === 'aiSuggested' && <span className={`text-base sm:text-xs font-bold ${color.text} opacity-50`}>{score.toFixed(1)}</span>}
                  </div>

                  {/* Action buttons — wrap on mobile */}
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    {teamMembers.length > 1 && (
                      <select
                        value={data.assignedTo || ''}
                        onChange={e => updateTouchpoint(tp.id, 'assignedTo', e.target.value)}
                        onClick={e => e.stopPropagation()}
                        className="text-[11px] sm:text-[8px] px-2 py-1.5 sm:py-0.5 rounded bg-neutral-800/40 text-neutral-500 border-0 focus:ring-1 focus:ring-brand-gold/30"
                      >
                        <option value="">Assign...</option>
                        {teamMembers.filter(m => m !== 'Unassigned').map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    )}

                    {state === 'default' && (
                      <>
                        <button onClick={activateTP} className="text-[11px] sm:text-[9px] px-3 sm:px-2 py-1.5 sm:py-0.5 rounded font-medium border bg-neutral-800/20 text-neutral-600 border-neutral-800 hover:text-neutral-400 active:bg-neutral-800/40 transition-colors">Evaluate</button>
                        <button onClick={markNotOffered} className="text-[11px] sm:text-[9px] px-3 sm:px-2 py-1.5 sm:py-0.5 rounded font-medium border bg-neutral-800/20 text-neutral-700 border-neutral-800 hover:text-neutral-400 active:bg-neutral-800/40 transition-colors">N/A</button>
                      </>
                    )}

                    {state === 'aiSuggested' && (
                      <>
                        <button onClick={approveTP} className="text-[11px] sm:text-[9px] px-3 sm:px-2 py-1.5 sm:py-0.5 rounded font-medium border bg-brand-gold/10 text-brand-gold border-brand-gold/30 hover:bg-brand-gold/20 active:bg-brand-gold/30 transition-colors">Approve</button>
                        <button onClick={markNotOffered} className="text-[11px] sm:text-[9px] px-3 sm:px-2 py-1.5 sm:py-0.5 rounded font-medium border bg-neutral-800/20 text-neutral-600 border-neutral-800 hover:text-neutral-400 transition-colors">N/A</button>
                      </>
                    )}

                    {state === 'scored' && (
                      <>
                        <span className="text-[11px] sm:text-[9px] px-2 py-1 sm:py-0.5 rounded font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">Approved</span>
                        <button onClick={markNotOffered} className="text-[11px] sm:text-[9px] px-2 py-1 sm:py-0.5 rounded text-neutral-700 hover:text-neutral-400 transition-colors">N/A</button>
                        <button onClick={resetTP} className="text-[11px] sm:text-[9px] px-2 py-1 sm:py-0.5 rounded text-neutral-700 hover:text-neutral-400 transition-colors">Reset</button>
                      </>
                    )}

                    {state === 'notOffered' && (
                      <>
                        <span className="text-[11px] sm:text-[9px] px-2 py-1 sm:py-0.5 rounded font-medium bg-neutral-700/40 text-neutral-400 border border-neutral-600">Not Offered</span>
                        <button onClick={resetTP} className="text-[11px] sm:text-[9px] px-2 py-1 sm:py-0.5 rounded text-neutral-700 hover:text-neutral-400 transition-colors">Reset</button>
                      </>
                    )}
                  </div>

                  {(state === 'scored' || state === 'aiSuggested') && (
                    <>
                      <p className="text-[10px] sm:text-[9px] text-neutral-600 mb-3 sm:mb-2">{tp.desc}</p>
                      <div className="py-1">
                        <TickSlider value={score} onChange={v => { updateTouchpoint(tp.id, 'score', v); if (!data.approved) approveTP(); }} showValue={false} />
                      </div>

                      {/* Stopwatch — for timed touchpoints */}
                      {tp.timed && (
                        <div className="bg-neutral-800/30 rounded-lg p-3 mt-2 flex items-center gap-3">
                          <span className="text-[10px] text-neutral-500 font-medium">Timer</span>
                          <span className={`text-lg sm:text-base font-mono font-bold ${timers[tp.id]?.running ? 'text-brand-gold' : data.measuredTime ? 'text-emerald-400' : 'text-neutral-600'}`}>
                            {timers[tp.id]?.elapsed ? formatTime(timers[tp.id].elapsed) : data.measuredTime ? data.measuredTime : '0:00'}
                          </span>
                          <div className="flex gap-1 ml-auto">
                            {!timers[tp.id]?.running ? (
                              <button onClick={() => startTimer(tp.id)} className="text-[10px] px-3 py-1.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 active:bg-emerald-500/20 transition-colors">
                                {timers[tp.id]?.elapsed || data.measuredTime ? 'Resume' : 'Start'}
                              </button>
                            ) : (
                              <button onClick={() => {
                                stopTimer(tp.id);
                                const time = formatTime(timers[tp.id]?.elapsed);
                                updateTouchpoint(tp.id, 'measuredTime', time);
                              }} className="text-[10px] px-3 py-1.5 rounded bg-red-500/10 text-red-400 border border-red-500/30 active:bg-red-500/20 transition-colors">
                                Stop
                              </button>
                            )}
                            {(timers[tp.id]?.elapsed > 0 || data.measuredTime) && !timers[tp.id]?.running && (
                              <button onClick={() => { resetTimer(tp.id); updateTouchpoint(tp.id, 'measuredTime', null); }} className="text-[10px] px-2 py-1.5 rounded text-neutral-600 hover:text-neutral-400 transition-colors">
                                Reset
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Noise meter — for ambiance */}
                      {tp.noiseMeter && (
                        <NoiseMeter
                          existingLevel={data.noiseLevel}
                          onCapture={db => updateTouchpoint(tp.id, 'noiseLevel', db)}
                        />
                      )}

                      {data.aiResearch && (
                        <div className="bg-brand-gold/5 border border-brand-gold/20 rounded p-3 sm:p-2 mt-2">
                          <p className="text-[10px] sm:text-[9px] text-brand-gold font-semibold mb-0.5">AI Research</p>
                          <p className="text-[11px] sm:text-[10px] text-neutral-400">{data.aiResearch}</p>
                        </div>
                      )}

                      <textarea
                        value={data.observation || ''}
                        onChange={e => updateTouchpoint(tp.id, 'observation', e.target.value)}
                        placeholder="Your observation..."
                        rows={2}
                        className="input-field text-sm sm:text-xs py-2 sm:py-1.5 mt-2 sm:mt-1.5"
                      />

                      {/* Copy to another visit */}
                      {visits.length > 1 && (
                        <div className="flex flex-wrap items-center gap-1 mt-2 sm:mt-1.5">
                          <span className="text-[9px] sm:text-[8px] text-neutral-700">Copy to:</span>
                          {visits.map((v, vi) => vi !== safeIndex && (
                            <button key={vi} onClick={() => copyToVisit(tp.id, vi)} className="text-[9px] sm:text-[8px] px-2 sm:px-1.5 py-1 sm:py-0.5 rounded bg-neutral-800/40 text-neutral-600 hover:text-brand-gold active:bg-neutral-800/60 transition-colors">{v.name}</button>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {state === 'notOffered' && (
                    <textarea
                      value={data.observation || ''}
                      onChange={e => updateTouchpoint(tp.id, 'observation', e.target.value)}
                      placeholder="Why not offered? (optional)..."
                      rows={1}
                      className="input-field text-sm sm:text-xs py-2 sm:py-1 mt-1 opacity-70"
                    />
                  )}

                  {state === 'default' && (
                    <p className="text-[10px] sm:text-[9px] text-neutral-700 mt-0.5">{tp.desc}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Phase summary when enough scored */}
      {approvedCount >= 5 && (
        <div className="card p-5 mt-4">
          <h3 className="text-sm font-semibold text-white mb-3">Journey Summary — {visitName}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {JOURNEY_PHASES.map(phase => {
              const phaseTPs = phase.touchpoints;
              const phaseScored = phaseTPs.filter(t => getState(t) === 'scored');
              const phaseAvg = phaseScored.length > 0 ? phaseScored.reduce((s, t) => s + journey[t.id].score, 0) / phaseScored.length : 0;
              const c = getScoreColor(phaseAvg);
              return (
                <div key={phase.phase} className="bg-neutral-800/30 rounded-lg p-2 text-center">
                  <div className="w-2 h-2 rounded-full mx-auto mb-1" style={{ backgroundColor: phase.color }} />
                  <p className={`text-sm font-bold ${c.text}`}>{phaseAvg > 0 ? phaseAvg.toFixed(1) : '—'}</p>
                  <p className="text-[8px] text-neutral-500">{phase.phase}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
